<template>
  <div class="container-box" :class="fontSize">
    <div class="dialogue-box" v-for="(item, index) in dataList" :key="index">
      <span v-if="item.role" class="dialogue-box_role">{{ item.role }}</span>
      <div class="dialogue-box_sentence" :data-paragraph-index="item.paragraphIndex">
        <img
          :src="item.showTranslate ? imageMap.icon_translate_active : imageMap.icon_translate"
          alt="翻译开关"
          @click="item.showTranslate = !item.showTranslate"
        />
        <span
          v-for="(s, idx) in item.paragraphText"
          :key="s.sentenceIndex"
          :data-sentence-index="s.sentenceIndex"
          class="sentence"
          :class="{ 'sentence-active': s.sentenceIndex === activeSentenceIndex }"
          @click="handleSentenceClick(s.sentenceIndex)"
          >{{ s.sentenceText }}</span
        >
        <div v-if="item.showTranslate" class="translate">
          <span v-for="(s, idx) in item.paragraphText" :key="s.sentenceIndex">
            {{ s.sentenceTranslation }}
          </span>
        </div>
      </div>
    </div>
    <Record
      v-if="!listenStore.listenPrepare"
      ref="recordRef"
      class="record-box"
      :ref-text="currentSentence"
      :overall="currentoverall"
      :aduio-src="sentenceAudio"
      :my-audio-url="myAudioUrl"
      @finishScore="handleScoreFinish"
    />
  </div>
</template>

<script setup>
import { nextTick, onMounted, ref, defineProps, watch, computed } from "vue"
import { useListenConfigStore } from "@/hooks/useListenConfigStore"
import Record from "./components/Record.vue"
import { imageConfig } from "@/views/listening/components/ThemeTag/imageConfig"
import store from "@/js/store"

// 状态管理与Props
const listenStore = useListenConfigStore()
const props = defineProps(["linkContent"])
const emit = defineEmits(["recordFinish"])

// 常量定义
const PASS_SCORE = 75 //
const OSS_URL = `${store.getOEMBASE() || ""}grammar_content/`

// 响应式状态
const dataList = ref([])
const activeSentenceIndex = ref(0) //
const sentenceAudio = ref("")
const currentSentence = ref("")
const currentoverall = ref(null)
const myAudioUrl = ref("")
const recordRef = ref(null)

// 计算属性
const fontSize = computed(() => listenStore.fontSize)
const theme = computed(() => listenStore.theme)
const imageMap = computed(() => imageConfig[theme.value])
const allSentences = computed(() => {
  // 扁平化所有句子，便于快速访问
  return dataList.value.flatMap((item) => item.paragraphText)
})

// 监听内容变化
watch(
  () => props.linkContent,
  (newVal) => {
    if (!newVal) return

    // 初始化数据列表
    dataList.value = newVal.map((item) => ({
      ...item,
      showTranslate: listenStore.showTranslate,
      paragraphText: item.paragraphText.map((p) => ({
        ...p,
        ...(p.studentAnswer && {
          scoreResult: {
            ...JSON.parse(p.studentAnswer.scoreResult).result,
            audioFile: p.studentAnswer.audioFile,
          },
        }),
      })),
    }))

    if (listenStore.listenPrepare) return

    // 找到第一个未通过的句子
    const firstUnpassed = allSentences.value.find(
      (p) => !p.studentAnswer || (p.scoreResult && p.scoreResult.overall < 70)
    )

    activeSentenceIndex.value = firstUnpassed?.sentenceIndex || 1

    nextTick(() => {
      const paragraphArr = document.querySelectorAll("[data-paragraph-index]") //获取段落
      paragraphArr.forEach((pDom, index) => {
        const sentenceArr = pDom.querySelectorAll(".sentence")
        sentenceArr.forEach((sDom, idx) => {
          let details = null
          if (dataList.value[index].paragraphText[idx].studentAnswer) {
            details = dataList.value[index].paragraphText[idx].scoreResult.details
          }
          sDom.innerHTML = splitWord(sDom.innerText, details)
        })
      })
      setSentenceActive(activeSentenceIndex.value)
    })
  }
)

// 监听翻译显示状态
watch(
  () => listenStore.showTranslate,
  (newVal) => {
    dataList.value.forEach((item) => {
      item.showTranslate = newVal
    })
  }
)

// 句子点击处理
const handleSentenceClick = (sentenceIndex) => {
  if (activeSentenceIndex.value === sentenceIndex) return

  activeSentenceIndex.value = sentenceIndex
  setSentenceActive(sentenceIndex)
  resetRecordState()
}

// 设置当前激活句子
const setSentenceActive = (sentenceIndex) => {
  const sentence = allSentences.value.find((s) => s.sentenceIndex === sentenceIndex)
  if (!sentence) return

  currentSentence.value = sentence.sentenceText
  sentenceAudio.value = sentence.sentenceAudio
  currentoverall.value = sentence.scoreResult ? sentence.scoreResult.overall : null
  myAudioUrl.value = sentence.scoreResult?.audioFile || sentence.studentAnswer?.audioFile || null
}

// 重置录音状态
const resetRecordState = () => {
  if (recordRef.value) {
    recordRef.value.resetRecord()
    recordRef.value.resetMyAudio()
    recordRef.value.resetAudio()
  }
}

// 处理评分完成
const handleScoreFinish = (score) => {
  emit("recordFinish", {
    score,
    titleNumber: activeSentenceIndex.value,
    audioFile: score.audioUrl,
  })

  // 更新当前句子评分结果
  const currentSentence = allSentences.value.find(
    (s) => s.sentenceIndex === activeSentenceIndex.value
  )

  if (currentSentence) {
    currentSentence.scoreResult = {
      ...score.result,
      audioFile: score.audioUrl,
    }
  }

  nextTick(() => {
    //获取当前句子的dom,渲染单词颜色
    const currentSentenceActive = document.querySelectorAll(".sentence-active")[0]
    currentSentenceActive.innerHTML = splitWord(currentSentence.sentenceText, score.result.details)

    //获取下一句
    const nextSentence = allSentences.value.find((s) => s.sentenceIndex > activeSentenceIndex.value)
    // 自动切换到下一句（如果通过）
    if (score.result.overall >= PASS_SCORE && nextSentence) {
      activeSentenceIndex.value = nextSentence.sentenceIndex
      setSentenceActive(activeSentenceIndex.value)
    } else {
      currentoverall.value = score.result.overall
      myAudioUrl.value = score.audioUrl
    }
  })
}

// 拆分单词并添加颜色标签
const splitWord = (sentence, details) => {
  if (!sentence) return ""

  return sentence
    .split(" ")
    .map((word, index) => {
      const score = details?.[index]?.score
      const pureWord = word.replace(/[.!?。！？,]\s*$/, "")
      const punctuation = word.replace(pureWord, "")

      return `${setColorLabel(pureWord, score)}${punctuation} `
    })
    .join("")
}

// 设置单词颜色标签
const setColorLabel = (word, score) => {
  if (score === undefined) return `<span>${word}</span>`

  if (score >= 80) {
    return `<span class="highlight-green">${word}</span>`
  } else if (score >= 60) {
    return `<span class="highlight-yellow">${word}</span>`
  } else {
    return `<span class="highlight-red">${word}</span>`
  }
}

defineExpose({
  active: activeSentenceIndex,
  dataList,
})

onMounted(() => {})
</script>

<style scoped lang="less">
@import "components/ThemeTag/fontSize";
.container-box {
  padding-bottom: 4rem;
  .dialogue-box {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 0.8rem;
    margin-bottom: 0.8rem;
    font-weight: 400;
    &_role {
      color: #ff8c00;
      font-size: 1.1rem;
      display: inline-block;
      min-width: 2.2rem;
      line-height: 1.7;
    }
    &:nth-child(2n) {
      .dialogue-box_role {
        color: #04976b;
      }
    }
    &_sentence {
      position: relative;
      font-size: var(--step3-text);
      color: var(--text-color);
      line-height: 1.7;
      img {
        width: 1.3rem;
        height: 1.3rem;
        vertical-align: middle;
        margin-right: 0.3rem;
      }
      .sentence {
        margin-right: 5px;
        cursor: pointer;
      }
      .translate {
        color: #898c90;
        font-size: var(--step3-translate-text);
        margin: 0.6rem 0 0;
      }
    }
    .sentence-active {
      background-color: var(--sentence-highligh-bg);
      color: var(--sentence-highligh-color);
      font-weight: 500;
      line-height: 1.7;
      span {
        color: white;
      }
    }
  }
  .record-box {
    position: fixed;
    bottom: 3.6rem;
    left: 50%;
    transform: translateX(-50%);
    width: 91%;
    align-items: center;
    height: 4rem;
  }
}
</style>
